import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import { useCallback, useMemo } from 'react';

import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import {
  generatePlaceHolderDisplay,
  OptionType,
} from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment, EnvironmentStatus } from 'dashboard/models/environment';
import { ExporterFileType } from 'dashboard/models/exporter';
import { List, ListStatus } from 'dashboard/models/list';
import { Tag } from 'dashboard/models/tag';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType, TransactionTypeFilterOption } from 'dashboard/models/transaction-type';
import { TransactionsFilterParams } from 'dashboard/redux/actions/transactions';
import CreateTransactionModal from 'dashboard/views/DashboardTransactionsView/CreateTransactionModal';
import TransactionsTable from 'dashboard/views/DashboardTransactionsView/TransactionsTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import IconTransactions from 'images/icons/accounts.svg';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface DashboardTransactionsViewProps {
  transactions: Transaction[];
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
  transactionTypeFilterOptions: TransactionTypeFilterOption[];
  transactionPaginationParams: PaginationParams;
  tags: Tag[];
  pageSize: number;
  transactionsFilters: TransactionsFilterParams;
  sortParams: SortParams;
  isLoadingTransactions: boolean;
  isLoadingTransactionExportCreate: boolean;
  productFeatureIsDisabled: boolean;
  currentEnvironment: Environment;
  lists: List[];
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleTagFilterUpdate: (tagIds: string[]) => void;
  handleTransactionTypeFilterUpdate: (transactionTypeIds: string[]) => void;
  handleTransactionStatusFilterUpdate: (transactionStatusIds: string[]) => void;
  handleTransactionsExportCreate: (exporterFileType: ExporterFileType) => Promise<void>;
  handleListFilterUpdate: (listIds: string[]) => void;
}

const DashboardTransactionsView = ({
  transactions,
  transactionStatuses,
  transactionTypes,
  transactionTypeFilterOptions,
  transactionPaginationParams,
  tags,
  pageSize,
  transactionsFilters,
  sortParams,
  isLoadingTransactions,
  currentEnvironment,
  lists,
  handlePageLinkClick,
  handlePageSizeClick,
  handleDateUpdate,
  handleOmniSearch,
  handleSortUpdate,
  handleTagFilterUpdate,
  handleTransactionTypeFilterUpdate,
  handleTransactionStatusFilterUpdate,
  handleTransactionsExportCreate,
  handleListFilterUpdate,
  productFeatureIsDisabled,
  isLoadingTransactionExportCreate,
}: DashboardTransactionsViewProps) => {
  const {
    createdAtStart,
    createdAtEnd,
    tagIds,
    omni,
    transactionTypeIds,
    transactionStatusIds,
    listIds,
  } = transactionsFilters;
  const [
    showCreateTransactionModal,
    { enable: openCreateTransactionModal, disable: hideCreateTransactionModal },
  ] = useToggle(false);

  const tagFilterOptions = useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.attributes.name,
        value: tag.id,
      })),
    [tags],
  );

  const transactionTypeNameFilterOptions = useMemo(
    () =>
      transactionTypeFilterOptions.map((filterOption) => ({
        label: filterOption.name,
        value: filterOption.id,
      })),
    [transactionTypeFilterOptions],
  );

  const transactionStatusFilterOptions = useMemo(() => {
    if (!transactionTypeFilterOptions.length) {
      return [];
    }

    const transactionStatusOptionsByTransactionTypeId = transactionStatuses.reduce(
      (result, ts) => ({
        ...result,
        [ts.relationships['transaction-type'].data.id]: [
          ...(result[ts.relationships['transaction-type'].data.id] || []),
          { label: ts.attributes.name, value: ts.id },
        ],
      }),
      {} as Record<TransactionStatus['id'], OptionType[]>,
    );

    return transactionTypeFilterOptions
      .filter((filterOption) => transactionStatusOptionsByTransactionTypeId[filterOption.id])
      .filter((filterOption) =>
        transactionTypeIds ? transactionTypeIds.includes(filterOption.id) : true,
      )
      .map((filterOption) => ({
        label: filterOption.name,
        options: transactionStatusOptionsByTransactionTypeId[filterOption.id],
        value: null,
      }));
  }, [transactionStatuses, transactionTypeFilterOptions, transactionTypeIds]);

  const listFilterOptions = useMemo(
    () =>
      (lists || [])
        .filter((list) => list.attributes.status === ListStatus.Active)
        .map((list) => ({
          label: list.attributes.name,
          value: list.id,
        })),
    [lists],
  );

  const listFilterLabelTextMapper = useCallback(
    (values: string[]) => {
      if (values.length === 0 || values.length === listFilterOptions.length) {
        return 'All lists';
      } else if (values.length === 1) {
        return listFilterOptions.find((v) => v.value === values[0])?.label;
      } else {
        return `Matching ${values.length} Lists`;
      }
    },
    [listFilterOptions],
  );

  const hasTransactionsListPermission = usePermission(Permission.TransactionList);
  const hasTransactionsViewPermission = usePermission(Permission.TransactionView);

  const isCurrentEnvironmentActive =
    currentEnvironment.attributes.status === EnvironmentStatus.Active;
  const disableCreateTransaction = !isCurrentEnvironmentActive;

  const showListMatchesFilter = useFeatureFlag('dashboard:decouple-list-matches-from-inquiries');

  if (productFeatureIsDisabled || !hasTransactionsListPermission) {
    return (
      <>
        <DisabledProductFeature
          title='Transactions'
          prompt='Transactions mirror interactions in your system.'
          helpLink={
            <a
              href='https://support.withpersona.com/hc/en-us/categories/360004567574-Product-guide'
              target='_blank'
              rel='noreferrer'
            >
              Learn more about transactions
            </a>
          }
          icon={<IconTransactions />}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          popupMessage={
            productFeatureIsDisabled && 'Your current plan does not have access to this feature.'
          }
        />
      </>
    );
  }

  const filterConfig: FilterConfig[] = [
    {
      id: 'dateCreated',
      name: 'Date created',
      type: 'daterange',
      onChange: handleDateUpdate,
      value: {
        startDate: createdAtStart,
        endDate: createdAtEnd,
      },
    },
    {
      id: 'type',
      name: 'Type',
      type: 'multiselect-alt',
      enabled: transactionTypeNameFilterOptions.length >= 1,
      placeholder: hasTransactionsViewPermission ? undefined : 'RESTRICTED',
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Type', multiple: 'Types' },
        emptyIsAll: true,
      }),
      options: transactionTypeNameFilterOptions,
      searchEnabled: true,
      selectAllEnabled: true,
      onChange: handleTransactionTypeFilterUpdate,
      value: transactionTypeIds ? transactionTypeIds : [],
    },
    {
      id: 'status',
      name: 'Status',
      type: 'multiselect-alt',
      enabled: transactionStatusFilterOptions.length >= 1,
      placeholder: hasTransactionsViewPermission ? undefined : 'RESTRICTED',
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Status', multiple: 'Statuses' },
        emptyIsAll: true,
      }),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      options: transactionStatusFilterOptions,
      selectAllEnabled: true,
      searchEnabled: true,
      onChange: handleTransactionStatusFilterUpdate,
      value: transactionStatusIds ? transactionStatusIds : [],
    },
    {
      id: 'listMatches',
      htmlId: 'txns-view__list-matches',
      name: 'List matches',
      type: 'multiselect-legacy',
      enabled: showListMatchesFilter && listFilterOptions.length > 1,
      selectOptionValues: listFilterOptions,
      labelTextMapper: listFilterLabelTextMapper,
      onChange: handleListFilterUpdate,
      value: listIds || [],
    },
    {
      id: 'tag',
      name: 'Tag',
      type: 'multiselect-alt',
      enabled: tagFilterOptions.length >= 1,
      placeholder: hasTransactionsViewPermission ? undefined : 'RESTRICTED',
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Tag', multiple: 'Tags' },
        emptyIsAll: true,
      }),
      options: tagFilterOptions,
      selectAllEnabled: true,
      searchEnabled: true,
      onChange: handleTagFilterUpdate,
      value: tagIds ? tagIds : [],
    },
  ];

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header data-test='transactions__header' title='Transactions'>
        <HStack spacing={Spacing[3]}>
          <Permissioned permission={Permission.TransactionCreate}>
            <Button
              id='transactions-view__create-transaction-button'
              name='create-transaction.button'
              prefixIcon={<PlusIcon />}
              onClick={openCreateTransactionModal}
              disabled={disableCreateTransaction}
            >
              Create Transaction
            </Button>
          </Permissioned>
          <Permissioned permission={Permission.TransactionsExport}>
            <DropdownMenu
              trigger={
                <Button
                  id='dashboard-transactions-view__export-button'
                  variant='basic'
                  disabled={isLoadingTransactionExportCreate || isLoadingTransactions}
                  prefixIcon={<TransferOutIcon />}
                >
                  Export
                </Button>
              }
              align='end'
            >
              {Object.values(ExporterFileType).map((fileType) => (
                <DropdownMenu.Item
                  id={`dashboard-transactions-view__export-${fileType}`}
                  onClick={() => {
                    handleTransactionsExportCreate(fileType);
                  }}
                  key={`dashboard-transactions-view__export-${fileType}`}
                >
                  {`To ${fileType}`}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          </Permissioned>
        </HStack>
      </Header>
      <Section>
        <CreateTransactionModal
          isOpen={showCreateTransactionModal}
          onClose={hideCreateTransactionModal}
          isLoadingTransactions={isLoadingTransactions}
          transactionStatuses={transactionStatuses}
          transactionTypes={transactionTypes}
        />

        <Card>
          <Card.Body>
            <Card.Title>Transactions</Card.Title>
            <TableControls
              searchProps={{
                id: 'transactions-table__search',
                handleInput: handleOmniSearch,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value: omni,
                placeholder: 'Search by transaction ID, reference ID, or external ID',
              }}
              filters={filterConfig}
            />
            <TransactionsTable
              hasUsedFilters={!Object.values(transactionsFilters).every(isEmpty)}
              transactionStatuses={transactionStatuses}
              transactions={transactions}
              transactionTypeFilterOptions={transactionTypeFilterOptions}
              loading={isLoadingTransactions}
              sortParams={sortParams}
              handleSortUpdate={handleSortUpdate}
            />
            {transactions.length !== 0 && (
              <Pagination
                pageSize={pageSize}
                paginationParams={transactionPaginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            )}
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardTransactionsView;
